<template>
<div class="modal is-large is-active">
    <div class="modal-background"></div>
    <div class="modal-content">
        <div class="custom-modal-box">
            <div v-if="loading" class="loader-container">
                <img src="/loader-dark.svg">
            </div>

            <div v-if="!loading">
                <div class="default-settings-title">
                    <span class="account-name">{{ getTitle }}</span>
                    <!-- <span class="default-settings-subtitle">Default backup settings</span> -->
                </div>
                <div class="default-settings-description">
                    Those will be your default settings when adding a new Droplet or volume, useful if you have to add a lot of servers with the same settings.
                    You can override these settings from the settings of each Cloudlet.
                </div>

                <div class="backup">
                    <div class="default-settings-title">
                        <span class="backup-setting-title">Frequency</span>
                        <span class="default-settings-subtitle">Configure how often we should create snapshots for your new cloudlets.</span>
                    </div>

                    <div class="columns">
                        <div class="column is-4">
                            <div class="field">
                                <div class="control has-icons-left">
                                    <div class="select is-fullwidth">
                                        <select v-model="frequency" :disabled="updating" name="predefined-schedule">
                                            <option v-for="frequencyOption in frequencyOptions" :value="frequencyOption.value">{{ frequencyOption.label }}</option>
                                        </select>
                                    </div>
                                    <div class="icon is-left is-custom-icon-left">
                                        <font-awesome-icon icon="history"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="column is-4">
                            <div class="field">
                                <div class="control has-icons-left tooltip is-tooltip-multiline" :data-tooltip="scheduleTooltip">
                                    <div class="select is-fullwidth">
                                        <select v-model="schedule" :disabled="updating" name="predefined-schedule">
                                            <option v-for="scheduleOption in scheduleOptions" :value="scheduleOption.value">{{ scheduleOption.label }}</option>
                                        </select>
                                    </div>
                                    <div class="icon is-left is-custom-icon-left">
                                        <font-awesome-icon icon="clock"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="column is-4">
                            <div class="field">
                                <div class="control has-icons-left">
                                    <div class="select is-fullwidth">
                                        <select v-model="timezone" :disabled="updating" name="predefined-schedule">
                                            <option v-for="timezone in timezones" :value="timezone.value">{{ timezone.label }}</option>
                                        </select>
                                    </div>
                                    <div class="icon is-left is-custom-icon-left">
                                        <font-awesome-icon icon="globe-americas"/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="default-settings-title m-top-50">
                        <span class="backup-setting-title">Retention</span>
                        <span class="default-settings-subtitle">Configure how long we should keep the snapshots of your new cloudlets.</span>
                    </div>

                    <div class="columns is-multiline">
                        <div v-if="multipleSnapshotsPerDay" class="column is-3">
                            <div @click="selectOption('frequentlyRetention')" class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': isSelected('frequentlyRetention'), 'backup-frequency-disabled': updating}">
                                <div class="icon">
                                    <font-awesome-icon icon="calendar-day"/>
                                </div>
                                <div class="details">
                                    <div class="detail-label">
                                        {{ frequentlyRetentionLabel }}
                                    </div>
                                    <div class="detail-value">
                                        {{ frequentlyBackupsRetentionLabel }}
                                    </div>
                                </diV>
                            </div>
                        </div>
                        <div class="column" :class="{'is-3': multipleSnapshotsPerDay, 'is-4': !multipleSnapshotsPerDay}">
                            <div @click="selectOption('dailyRetention')" class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': isSelected('dailyRetention'), 'backup-frequency-disabled': updating}">
                                <div class="icon">
                                    <font-awesome-icon icon="calendar-day"/>
                                </div>
                                <div class="details">
                                    <div class="detail-label">
                                        Daily backups ret.
                                    </div>
                                    <div class="detail-value">
                                        {{ dailyBackupsRetentionLabel }}
                                    </div>
                                </diV>
                            </div>
                        </div>
                        <div class="column" :class="{'is-3': multipleSnapshotsPerDay, 'is-4': !multipleSnapshotsPerDay}">
                            <div @click="selectOption('weeklyRetention')" class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': isSelected('weeklyRetention'), 'backup-frequency-disabled': updating}">
                                <div class="icon">
                                    <font-awesome-icon icon="calendar-week"/>
                                </div>
                                <div class="details">
                                    <div class="detail-label">
                                        Weekly backups ret.
                                    </div>
                                    <div class="detail-value">
                                        {{ weeklyBackupsRetentionLabel }}
                                    </div>
                                </diV>
                            </div>
                        </div>
                        <div class="column" :class="{'is-3': multipleSnapshotsPerDay, 'is-4': !multipleSnapshotsPerDay}">
                            <div @click="selectOption('monthlyRetention')" class="backup-retention backup-retention-selectable" :class="{'backup-frequency-selected': isSelected('monthlyRetention'), 'backup-frequency-disabled': updating}">
                                <div class="icon">
                                    <font-awesome-icon icon="calendar"/>
                                </div>
                                <div class="details">
                                    <div class="detail-label">
                                        Monthly backups ret.
                                    </div>
                                    <div class="detail-value">
                                        {{ monthlyBackupsRetentionLabel }}
                                    </div>
                                </diV>
                            </div>
                        </div>

                        <div class="column is-12">
                            <span style="font-size: 13px;">The last {{ selectedRetentionValue }} snapshots of type <b>"{{ selectedRetentionLabel }}"</b> will be retained by Weap. Use the buttons below to decrease/increase the amount.</span>
                        </div>

                        <div class="column is-12 has-text-centered" style="margin-top: 10px; margin-bottom: -10px;">
                            <div @click="decreaseSelectedRetention" class="button is-default-custom minus-button" :disabled="updating"><font-awesome-icon icon="minus"/></div>
                            <div @click="increaseSelectedRetention" class="button is-default-custom plus-button" :disabled="updating"><font-awesome-icon icon="plus"/></div>
                        </div>

                        <div class="column is-12 m-top-30">
                            <label class="checkbox tooltip is-tooltip-multiline" style="margin-top: 16px;" data-tooltip="If checked, the backup settings of all databases backed up through this server will be updated as well.">
                            <input v-model="applyChangeToDatabases" type="checkbox">
                            <span style="font-size: 14px; padding-top: 3px; padding-left: 5px;">Also update the backup settings of the databases belonging to this server</span>
                        </label>
                        </div>
                    </div>
                </div>

                <div class="columns is-multiline">
                    <div class="column is-3">
                        <div @click="updateDefaultDatabaseBackupSettings" :class="{ 'is-loading': updating }" :disabled="updating" class="button custom-button-size is-fullwidth is-info">Save</div>
                    </div>
                    <div class="column is-3">
                        <div @click="close" :disabled="updating" class="close-add-monitor-modal button custom-button-size is-fullwidth is-danger">Cancel</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <button @click="close" class="modal-close is-large" aria-label="close"></button>
</div>
</template>

<script>
import Api from '@/api';
import Swal from 'sweetalert2';

export default {
    props: ['server'],

    data() {
        return {
            loading: false,
            updating: false,
            selectedOption: 'frequentlyRetention',
            account: null,
            frequency: null,
            schedule: null,
            timezone: 'UTC',
            frequentlyRetention: null,
            dailyRetention: null,
            weeklyRetention: null,
            monthlyRetention: null,
            applyChangeToDatabases: false,
            frequencyOptions: [
                { label: 'Every hour', value: 1},
                { label: 'Every 2 hours', value: 2},
                { label: 'Every 3 hours', value: 3},
                { label: 'Every 4 hours', value: 4},
                // { label: 'Every 5 hours', value: 5},
                { label: 'Every 6 hours', value: 6},
                // { label: 'Every 8 hours', value: 8},
                { label: 'Every 12 hours', value: 12},
                { label: 'Once per day', value: 24},
                // { label: 'Once per week', value: 168},
                // { label: 'Once per month', value: 730},
            ],
            scheduleOptions: [
                { label: '00:00', value: '00:00' },
                { label: '01:00', value: '01:00' },
                { label: '02:00', value: '02:00' },
                { label: '03:00', value: '03:00' },
                { label: '04:00', value: '04:00' },
                { label: '05:00', value: '05:00' },
                { label: '06:00', value: '06:00' },
                { label: '07:00', value: '07:00' },
                { label: '08:00', value: '08:00' },
                { label: '09:00', value: '09:00' },
                { label: '10:00', value: '10:00' },
                { label: '11:00', value: '11:00' },
                { label: '12:00', value: '12:00' },
                { label: '13:00', value: '13:00' },
                { label: '14:00', value: '14:00' },
                { label: '15:00', value: '15:00' },
                { label: '16:00', value: '16:00' },
                { label: '17:00', value: '17:00' },
                { label: '18:00', value: '18:00' },
                { label: '19:00', value: '19:00' },
                { label: '20:00', value: '20:00' },
                { label: '21:00', value: '21:00' },
                { label: '22:00', value: '22:00' },
                { label: '23:00', value: '23:00' },
            ],
            timezones: [
                { label: 'UTC', value: 'UTC' },
            ],
        }
    },

    mounted() {
        this.listenEvents();
        this.initializeModels();
    },

    methods: {
        close() {
            Event.$emit('changeServerDefaultDatabaseBackupSettingsModalClosed');
        },

        listenEvents() {
            //
        },

        updateDefaultDatabaseBackupSettings() {
            if (this.loading || this.updating) {
                return false;
            }

            this.updating = true;

            let endpoint = 'servers/' + this.server.id + '/default-backup-settings';

            Api().put(endpoint, this.updatedDefaultDatabaseBackupSettings)
                .then(response => {
                    Event.$emit('serverDefaultDatabaseBackupSettingsChanged', () => {
                        Swal.fire({
                            title: 'Great!',
                            text: 'The default backup settings of the server have been updated.',
                            type: 'success',
                        });
                        this.updating = false;
                    });
                })
                .catch(error => {
                    //
                });
        },

        decreaseSelectedRetention() {
            if (this.updating) {
                return;
            }

            if (this.isSelected('frequentlyRetention') && this.frequentlyRetention > 0) {
                this.frequentlyRetention--;
                return;
            }

            if (this.isSelected('dailyRetention') && this.dailyRetention > 0) {
                this.dailyRetention--;
                return;
            }

            if (this.isSelected('weeklyRetention') && this.weeklyRetention > 0) {
                this.weeklyRetention--;
                return;
            }

            if (this.isSelected('monthlyRetention') && this.monthlyRetention > 0) {
                this.monthlyRetention--;
                return;
            }
        },

        increaseSelectedRetention() {
            if (this.updating) {
                return;
            }

            if (this.isSelected('frequentlyRetention')) {
                this.frequentlyRetention++;
                return;
            }

            if (this.isSelected('dailyRetention')) {
                this.dailyRetention++;
                return;
            }

            if (this.isSelected('weeklyRetention')) {
                this.weeklyRetention++;
                return;
            }

            if (this.isSelected('monthlyRetention')) {
                this.monthlyRetention++;
                return;
            }
        },

        isSelected(option) {
            return this.selectedOption === option;
        },

        selectOption(option) {
            if (this.updating) {
                return;
            }
            this.selectedOption = option;
        },

        reset() {
            this.account = null;
            this.selectedOption = 'frequentlyRetention';

            this.frequency = null;
            this.schedule = null;
            this.timezone = 'UTC';

            this.dailyRetention = null;
            this.weeklyRetention = null;
            this.monthlyRetention = null;
        },

        initializeModels() {
            if (this.server == null) {
                return;
            }

            this.frequency = this.server.default_database_frequency.hours;
            this.schedule = this.server.default_database_frequency.start_at;

            this.frequentlyRetention = this.server.default_database_retention.frequently;
            this.dailyRetention = this.server.default_database_retention.daily;
            this.weeklyRetention = this.server.default_database_retention.weekly;
            this.monthlyRetention = this.server.default_database_retention.monthly;
        },
    },

    computed: {
        updatedDefaultDatabaseBackupSettings() {
            return {
                default_database_frequency: {
                    hours: this.frequency,
                    start_at: this.schedule,
                },

                default_database_retention: {
                    frequently: this.frequentlyRetention,
                    daily: this.dailyRetention,
                    weekly: this.weeklyRetention,
                    monthly: this.monthlyRetention,
                },

                apply_change_to_databases: this.applyChangeToDatabases,
            };
        },

        getTitle() {
            if (this.server == null) {
                return '...';
            }

            return this.server.name;
        },

        frequencyLabel() {
            if (this.frequency == null) {
                return '';
            }

            if (this.frequency == 24) {
                return 'Once per day';
            }

            if (this.freuency == 168){
                return 'Once per week';
            }

            var label = 'Every ' + this.frequency;

            if (this.frequency > 1) {
                label += ' hours';
            } else {
                label += ' hour';
            }

            return label;
        },

        frequentlyBackupsRetentionLabel() {
            if (this.frequentlyRetention == null) {
                return '-';
            }

            return 'Last ' + this.frequentlyRetention;
        },

        dailyBackupsRetentionLabel() {
            if (this.dailyRetention == null) {
                return '-';
            }

            if (this.dailyRetention == 0) {
                return 'No daily retention';
            }

            if (this.dailyRetention == 1) {
                return 'The last one';
            }

            return 'Last ' + this.dailyRetention;
        },

        weeklyBackupsRetentionLabel() {
            if (this.weeklyRetention == null) {
                return '-';
            }

            if (this.weeklyRetention == 0) {
                return 'No weekly ret.';
            }

            if (this.weeklyRetention == 1) {
                return 'The last one';
            }

            return 'Last ' + this.weeklyRetention ;
        },

        monthlyBackupsRetentionLabel() {
            if (this.monthlyRetention == null) {
                return '-';
            }

            if (this.monthlyRetention == 0) {
                return 'No monthly ret.';
            }

            if (this.monthlyRetention == 1) {
                return 'The last one';
            }

            return 'Last ' + this.monthlyRetention;
        },

        scheduleTooltip() {
            if (this.frequency >= 24) {
                return 'The snapshot will be created ' + this.frequencyLabel.charAt(0).toLowerCase() + this.frequencyLabel.slice(1) + ' at ' + this.schedule + ' UTC';
            }

            return 'The snapshot will be created ' + this.frequencyLabel.charAt(0).toLowerCase() + this.frequencyLabel.slice(1) + ' starting from ' + this.schedule + ' UTC in the first day.';
        },

        multipleSnapshotsPerDay() {
            return this.frequency < 24;
        },

        selectedRetentionValue() {
            return this[this.selectedOption];
        },

        frequentlyRetentionLabel() {
            for (var i = 0; i < this.frequencyOptions.length; i++) {
                if (this.frequencyOptions[i].value == this.frequency) {
                    return this.frequencyOptions[i].label;
                }
            }

            return null;
        },

        selectedRetentionLabel() {
            if (this.isSelected('frequentlyRetention')) {
                for (var i = 0; i < this.frequencyOptions.length; i++) {
                    if (this.frequencyOptions[i].value == this.frequency) {
                        return this.frequencyOptions[i].label;
                    }
                }
            }

            if (this.isSelected('dailyRetention')) {
                return 'Daily';
            }

            if (this.isSelected('weeklyRetention')) {
                return 'Weekly';
            }

            if (this.isSelected('monthlyRetention')) {
                return 'Monthly';
            }
        },
    },

    watch: {
        server: function (newServer) {
            //
        },

        frequency(frequency) {
            if (frequency >= 24 && this.isSelected('frequentlyRetention')) {
                this.selectOption('dailyRetention');
            }
        },
    }
}
</script>
